import React from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useFcmRegistration } from '../../hooks/useFcmRegistration';
import { useGlobalHaatRequestsNotifications } from '../../hooks/useGlobalHaatRequestsNotifications';
import { CompleteProfileModal } from '../dashboard';

/** Role hierarchy for permission checking */
const ROLE_HIERARCHY: Record<string, number> = {
  user: 1,
  admin: 2,
  super_admin: 3,
} as const;

interface ProtectedRouteProps {
  /** Minimum required role to access this route */
  requiredRole?: 'user' | 'admin' | 'super_admin';
}

/** FCM + HAAT toasts only after profile is complete — avoids 403 on /permissions/my-permissions. */
const AuthedShell: React.FC = () => {
  useFcmRegistration();
  useGlobalHaatRequestsNotifications();
  return <Outlet />;
};

/**
 * ProtectedRoute Component
 * 
 * Guards routes that require authentication.
 * Optionally checks for minimum role level.
 * 
 * Features:
 * - Redirects unauthenticated users to login
 * - Saves attempted location for post-login redirect
 * - Role-based access control with hierarchy
 * - Blocks SSO self-provisioned users until phone + last 4 ID digits are saved
 * - Platform-wide HAAT Requests realtime notifications
 * 
 * @example
 * // Basic protection
 * <Route element={<ProtectedRoute />}>
 *   <Route path="/dashboard" element={<Dashboard />} />
 * </Route>
 * 
 * // With role requirement
 * <Route element={<ProtectedRoute requiredRole="admin" />}>
 *   <Route path="/admin" element={<AdminPanel />} />
 * </Route>
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ requiredRole }) => {
  const { isAuthenticated, isLoading, user, refreshUser } = useAuth();
  const location = useLocation();

  // Show loading spinner while checking auth
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-haat-bg-light">
        <div 
          className="animate-spin rounded-full h-12 w-12 border-4 border-haat-red border-t-transparent"
          role="status"
          aria-label="Loading"
        />
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check role-based access
  if (requiredRole && user) {
    const userLevel = ROLE_HIERARCHY[user.role] ?? 0;
    const requiredLevel = ROLE_HIERARCHY[requiredRole] ?? 0;

    if (userLevel < requiredLevel) {
      return <Navigate to="/unauthorized" replace />;
    }
  }

  const needsProfile =
    !!user?.selfProvisioned && (!user.phone || !user.hasIdNumber);

  if (needsProfile) {
    return (
      <CompleteProfileModal
        isOpen
        onCompleted={async () => {
          await refreshUser();
        }}
      />
    );
  }

  return <AuthedShell />;
};

export default ProtectedRoute;
